import { useEffect, useRef } from "react";

/**
 * Calls `callback` after `delay` ms of inactivity. Resets the timer on each call.
 */
export function useDebouncedCallback(
  callback: () => void,
  delay: number,
): { trigger: () => void; flush: () => void; cancel: () => void } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const trigger = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      callbackRef.current();
      timerRef.current = null;
    }, delay);
  };

  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      callbackRef.current();
    }
  };

  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return { trigger, flush, cancel };
}
